/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { toast } from "../../components/ui/sonner";
import { Loader2, TrendingUp, AlertTriangle, Sparkles, RefreshCw, FolderKanban, Building2, HardHat, PenTool, Warehouse, Landmark } from "lucide-react";
import { fmtDT } from "../../lib/date";
import { inp } from "./hrmsShared";

const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();

export default function HRMSPlanning() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const [org, setOrg] = useState(null);
  const [wl, setWl] = useState(null);
  const [link, setLink] = useState(null);
  const [insight, setInsight] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  // 2026-10-14 新增（对应 HR 反馈「Workforce Planning 页没有任何地方可编辑」）：
  //   HR 可录入/编辑本分公司的「人力规划备注」，保存到后端 /hrms/planning/notes。
  const [plan, setPlan] = useState(null);
  const [noteBranch, setNoteBranch] = useState("");
  const [noteDirty, setNoteDirty] = useState({});
  const [savingNote, setSavingNote] = useState(false);
  const reload = useCallback(() => {
    api.get("/hrms/org").then(({ data }) => setOrg(data)).catch(() => setOrg({ branches: [], alerts: [] }));
    api.get("/hrms/workload").then(({ data }) => setWl(data)).catch(() => setWl({ workload: [], unmatchedNames: [] }));
    api.get("/hrms/link/overview").then(({ data }) => setLink(data)).catch(() => setLink(null));
    api.get("/hrms/planning/notes").then(({ data }) => setPlan(data)).catch(() => setPlan({ notes: {}, canManage: false }));
  }, [api]);
  useEffect(() => { reload(); }, [reload]);
  const nbBranch = () => plan?.scopeBranch || noteBranch || (org?.branches?.[0]?.id || "");
  const nbValue = (b) => (noteDirty[b] !== undefined ? noteDirty[b] : (plan?.notes?.[b] || ""));
  const saveNote = async () => {
    const b = nbBranch();
    setSavingNote(true);
    try {
      await api.put("/hrms/planning/notes", { branch: b, note: nbValue(b) });
      toast.success(T("规划备注已保存", "Planning note saved"));
      const { data } = await api.get("/hrms/planning/notes"); setPlan(data); setNoteDirty({});
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setSavingNote(false);
  };

  const loadInsight = async (refresh = 0) => {
    setAiBusy(true);
    try { const { data } = await api.get("/hrms/ai-insight", { params: { refresh } }); setInsight(data); }
    catch (e) { toast.error(e?.response?.data?.detail || T("AI 洞察生成失败", "AI insight failed")); }
    setAiBusy(false);
  };

  if (!org || !wl) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  const totApproved = org.branches.reduce((s, b) => s + b.departments.reduce((s2, d) => s2 + d.positions.reduce((s3, p) => s3 + p.approved, 0), 0), 0);
  const totActual = org.branches.reduce((s, b) => s + b.total, 0);
  return (
    <div className="space-y-4" data-testid="hrms-planning-page">
      <div>
        <h1 className="text-xl font-extrabold text-slate-800">{T("人力规划", "Workforce Planning")}</h1>
        <p className="text-xs text-slate-400 mt-0.5">{T("项目驱动的人力需求：编制执行 · 超缺编预警 · 项目人力负载 · AI 洞察建议（设计方案模块 02）", "Project-driven staffing: headcount execution · over/under alerts · project workload · AI advice")}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-plan-stat-approved">
          <p className="text-xs text-slate-500">{T("批准编制", "Approved Headcount")}</p><p className="text-2xl font-extrabold text-slate-800 mt-1">{totApproved}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-plan-stat-actual">
          <p className="text-xs text-slate-500">{T("实际在编", "Actual Staff")}</p><p className="text-2xl font-extrabold text-slate-800 mt-1">{totActual}</p>
        </div>
        <div className={`rounded-xl border bg-white p-4 ${org.alerts.filter((a) => a.type === "over").length ? "border-red-300" : "border-slate-200"}`} data-testid="hrms-plan-stat-over">
          <p className="text-xs text-slate-500">{T("超编岗位", "Overstaffed")}</p><p className="text-2xl font-extrabold text-red-600 mt-1">{org.alerts.filter((a) => a.type === "over").length}</p>
        </div>
        <div className={`rounded-xl border bg-white p-4 ${org.alerts.filter((a) => a.type === "under").length ? "border-amber-300" : "border-slate-200"}`} data-testid="hrms-plan-stat-under">
          <p className="text-xs text-slate-500">{T("缺编岗位", "Understaffed")}</p><p className="text-2xl font-extrabold text-amber-600 mt-1">{org.alerts.filter((a) => a.type === "under").length}</p>
        </div>
      </div>

      {/* HR 可编辑的人力规划备注（对应需求「本页没有任何地方可编辑」） */}
      {plan?.canManage && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="hrms-plan-notes">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <p className="text-sm font-bold text-indigo-800">{T("人力规划备注（HR 可编辑）", "Workforce planning note (editable by HR)")}</p>
            {!plan.scopeBranch && (
              <select data-testid="hrms-plan-note-branch" className="text-xs rounded-md border border-slate-200 px-2 py-1" value={nbBranch()} onChange={(e) => setNoteBranch(e.target.value)}>
                {org.branches.map((b) => <option key={b.id} value={b.id}>{zh ? b.zh : (b.en || b.zh)}</option>)}
              </select>
            )}
            <button data-testid="hrms-plan-note-save" disabled={savingNote} onClick={saveNote} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-xs rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5 disabled:opacity-50">
              {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}{T("保存备注", "Save note")}
            </button>
          </div>
          <textarea data-testid="hrms-plan-note-text" rows={3} className={inp} value={nbValue(nbBranch())} onChange={(e) => setNoteDirty((s) => ({ ...s, [nbBranch()]: e.target.value }))}
            placeholder={T("例如：Q4 重点补 2 名 PM、圣地亚哥会计岗位暂缓、缺编岗位优先内部转岗…", "e.g. hire 2 PMs in Q4, pause the SD accountant role, prioritize internal transfers for understaffed roles…")} />
        </div>
      )}

      {org.alerts.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-3.5" data-testid="hrms-plan-alerts">
          <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5 mb-1.5"><AlertTriangle className="h-3.5 w-3.5" />{T("编制预警（在组织架构页可调整编制数）", "Staffing alerts (adjust headcount on Org page)")}</p>
          <div className="flex flex-wrap gap-1.5">
            {org.alerts.map((a, i) => (
              <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${a.type === "over" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                {a.branchZh} · {a.roleZh}：{a.type === "over" ? T("超编", "over") : T("缺编", "under")} {a.actual}/{a.approved}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4" data-testid="hrms-plan-ai">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-violet-600" />
          <p className="text-sm font-bold text-violet-800">{T("AI 人力洞察", "AI Workforce Insight")}</p>
          {insight?.at && <span className="text-[11px] text-slate-400">{insight.cached ? T("缓存 · ", "cached · ") : ""}{fmtDT(insight.at)}</span>}
          <button data-testid="hrms-plan-ai-refresh" disabled={aiBusy} onClick={() => loadInsight(1)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-[11px] rounded-full border border-violet-300 text-violet-600 hover:bg-violet-100 font-semibold px-3 py-1 disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${aiBusy ? "animate-spin" : ""}`} />{T("重新分析", "Re-analyze")}
          </button>
        </div>
        {insight ? (
          <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed" data-testid="hrms-plan-ai-text">{insight.text}</pre>
        ) : (
          <button data-testid="hrms-plan-ai-load" disabled={aiBusy} onClick={() => loadInsight(0)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-4 py-2 disabled:opacity-50">
            {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("生成洞察（编制缺口 / 项目负载 / 风险建议）", "Generate insight (headcount gaps / project load / risk advice)")}
          </button>
        )}
      </div>

      {link && (
        <>
          <div className="rounded-xl border border-slate-200 bg-white" data-testid="hrms-link-costcenter">
            <div className="flex-wrap md:flex-nowrap px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-400" />
              <p className="text-sm font-bold text-slate-700">{T(`分公司成本中心（法人 × 人力 × 收款打通 · ${link.month}）`, `Branch Cost Centers (entity × staffing × collections · ${link.month})`)}</p>
            </div>
            <div className="overflow-x-auto">
              <ResponsiveTable mobile="scroll" className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
                  <th className="px-3 py-2">{T("法人/分公司", "Entity")}</th><th className="px-3 py-2">{T("负责人", "Leader")}</th><th className="px-3 py-2">{T("在编", "Staff")}</th>
                  <th className="px-3 py-2">{T("月薪酬成本", "Payroll/mo")}</th><th className="px-3 py-2">{T("本月收款", "Collected (mo)")}</th><th className="px-3 py-2">{T("累计收款", "Collected (total)")}</th>
                  <th className="px-3 py-2">{T("在建项目", "Active Projects")}</th><th className="px-3 py-2">{T("延期", "Delayed")}</th><th className="px-3 py-2">{T("人力成本率", "Labor Ratio")}</th>
                </tr></thead>
                <tbody>
                  {link.branches.map((b) => (
                    <tr key={b.id} className="border-b border-slate-50" data-testid={`hrms-link-branch-${b.id}`}>
                      <td className="px-3 py-2 font-semibold text-slate-700">{zh ? b.zh : (b.en || b.zh)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{b.leader || "-"}</td>
                      <td className="px-3 py-2">{b.headcount}</td>
                      <td className="px-3 py-2">{b.payrollMonthly ? fmt(b.payrollMonthly) : <span className="text-slate-300 text-xs">{T("未设薪酬", "not set")}</span>}</td>
                      <td className="px-3 py-2">{fmt(b.collectedMonth)}</td>
                      <td className="px-3 py-2 text-slate-500">{fmt(b.collectedTotal)}</td>
                      <td className="px-3 py-2">{b.activeProjects}</td>
                      <td className="px-3 py-2">{b.delayed ? <span className="text-red-600 font-bold">{b.delayed}</span> : "-"}</td>
                      <td className="px-3 py-2">{b.laborRatioPct != null ? <span className={`font-bold ${b.laborRatioPct > 50 ? "text-red-600" : "text-emerald-600"}`}>{b.laborRatioPct}%</span> : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>
            </div>
            <p className="px-4 py-2 text-[11px] text-slate-400">{T("薪酬成本取自薪酬档案（月薪或时薪×173.3h）；收款按项目 team 归属法人实时汇总；在「薪酬激励 → 薪酬档案」维护底薪后成本率自动生效。", "Payroll from compensation records (monthly or hourly ×173.3h); collections aggregated live by project team entity; labor ratio activates once base pay is maintained in Compensation.")}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hrms-link-depts">
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-link-eng">
              <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><HardHat className="h-3.5 w-3.5 text-orange-500" />{T("工程部", "Construction Dept")} · {link.departments.engineering.headcount} {T("人", "")}</p>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                <p className="flex justify-between">{T("在建项目", "Active projects")}<b className="text-slate-700">{link.departments.engineering.activeProjects}</b></p>
                <p className="flex justify-between">{T("PM 人数 / 人均负载", "PMs / avg load")}<b className="text-slate-700">{link.departments.engineering.pms} / {link.departments.engineering.avgLoad}</b></p>
                <p className="flex justify-between">{T("延期项目", "Delayed")}<b className={link.departments.engineering.delayed ? "text-red-600" : "text-slate-700"}>{link.departments.engineering.delayed}</b></p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-link-design">
              <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><PenTool className="h-3.5 w-3.5 text-violet-500" />{T("设计部", "Design")} · {link.departments.design.headcount} {T("人", "")}</p>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                <p className="flex justify-between">{T("未完成任务", "Open tasks")}<b className="text-slate-700">{link.departments.design.openTasks}</b></p>
                <p className="flex justify-between">{T("本月完成", "Done this month")}<b className="text-slate-700">{link.departments.design.completedMonth}</b></p>
                <p className="flex justify-between">{T("阻塞任务", "Blocked")}<b className={link.departments.design.blocked ? "text-red-600" : "text-slate-700"}>{link.departments.design.blocked}</b></p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-link-warehouse">
              <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Warehouse className="h-3.5 w-3.5 text-emerald-600" />{T("仓储部", "Warehouse")} · {link.departments.warehouse.headcount} {T("人", "")}</p>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                <p className="flex justify-between">{T("库存货值", "Inventory value")}<b className="text-slate-700">{fmt(link.departments.warehouse.inventoryValue)}</b></p>
                <p className="flex justify-between">{T("低库存预警", "Low stock alerts")}<b className={link.departments.warehouse.lowStock ? "text-amber-600" : "text-slate-700"}>{link.departments.warehouse.lowStock}</b></p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-link-finance">
              <p className="text-xs font-bold text-slate-600 flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5 text-sky-600" />{T("财务部", "Finance")} · {link.departments.finance.headcount} {T("人", "")}</p>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                <p className="flex justify-between">{T("待处理付款申请", "Pending payment requests")}<b className={link.departments.finance.pendingPaymentRequests ? "text-amber-600" : "text-slate-700"}>{link.departments.finance.pendingPaymentRequests}</b></p>
                <p className="flex justify-between">{T("未结账单", "Unpaid bills")}<b className="text-slate-700">{link.departments.finance.unpaidBills} · {fmt(link.departments.finance.unpaidAmount)}</b></p>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="rounded-xl border border-slate-200 bg-white" data-testid="hrms-plan-workload">
        <div className="flex-wrap md:flex-nowrap px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-slate-400" />
          <p className="text-sm font-bold text-slate-700">{T("项目人力负载排行（在建项目）", "Project Workload Ranking (active projects)")}</p>
          {wl.unmatchedNames?.length > 0 && <span className="ml-auto text-[11px] text-amber-600">{T("未匹配档案的项目负责人：", "Unmatched project leads: ")}{wl.unmatchedNames.slice(0, 5).join("、")}{wl.unmatchedNames.length > 5 ? T(" 等", " …") : ""}</span>}
        </div>
        <ResponsiveTable mobile="scroll" className="w-full text-sm">
          <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
            <th className="px-3 py-2">{T("员工", "Employee")}</th><th className="px-3 py-2">{T("岗位", "Role")}</th><th className="px-3 py-2">{T("PM 在管", "PM projects")}</th>
            <th className="px-3 py-2">{T("销售跟进", "Sales projects")}</th><th className="px-3 py-2">{T("合计", "Total")}</th><th className="px-3 py-2">{T("负载", "Load")}</th>
          </tr></thead>
          <tbody>
            {wl.workload.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-slate-300">{T("暂无负载数据", "No workload data")}</td></tr> :
              wl.workload.map((w) => {
                const max = wl.workload[0]?.total || 1;
                return (
                  <tr key={w.employeeId} className="border-b border-slate-50" data-testid={`hrms-wl-${w.employeeId}`}>
                    <td className="px-3 py-2 font-semibold text-slate-700">{w.name}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{w.role}</td>
                    <td className="px-3 py-2">{w.pmProjects || "-"}</td>
                    <td className="px-3 py-2">{w.salesProjects || "-"}</td>
                    <td className="px-3 py-2 font-bold text-slate-800">{w.total}</td>
                    <td className="px-3 py-2 w-40">
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${w.total / max > 0.75 ? "bg-red-500" : w.total / max > 0.4 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${Math.round((w.total / max) * 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </ResponsiveTable>
      </div>
      <p className="text-[11px] text-slate-400 flex items-center gap-1"><FolderKanban className="h-3 w-3" />{T("负载数据实时来自现有项目管理模块（PM/销售姓名匹配），编制在「组织架构」页维护。", "Workload is live from the project module (matched by PM/sales name); headcount is maintained on the Org page.")}</p>
    </div>
  );
}
